import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column } from 'react-table';

import { BannerStyle } from '../../../../typings/enums';
import { useDialogContext } from '../../../contexts/DialogContext';
import {
  getVcsGitHubMigrationPreviewQueryKey,
  useGetVcsGitHubMigrationPreview,
} from '../../../hooks/api/internalTools/vcsGitHub/useGetVcsGitHubMigrationPreview';
import { useGetVcsGitHubStatus } from '../../../hooks/api/internalTools/vcsGitHub/useGetVcsGitHubStatus';
import { useMigrateVcsGitHubOrg } from '../../../hooks/api/internalTools/vcsGitHub/useMigrateVcsGitHubOrg';
import { useRollbackVcsGitHubOrg } from '../../../hooks/api/internalTools/vcsGitHub/useRollbackVcsGitHubOrg';
import queryClient from '../../../ReactQuery/client';
import { getErrorMessage } from '../../../ReactQuery/errors';
import Accordion from '../../generic/Accordion';
import Badge from '../../generic/Badge/Badge';
import Banner from '../../generic/Banner';
import Button from '../../generic/Button/Button';
import { BUTTON_TYPE } from '../../generic/Button/button-constants';
import Card from '../../generic/Card/Card';
import { Divider } from '../../generic/Divider';
import Flex from '../../generic/Flex';
import Loader from '../../generic/Loader';
import SimpleReactTable from '../../generic/SimpleReactTable';
import Text from '../../generic/Text';
import type {
  VcsGitHubInstallationResult,
  VcsGitHubMigrationJob,
  VcsGitHubMigrationState,
} from '../../../../shared/types/routes/internalTools/vcsGitHub';
import type { Organization } from '../../../types';
import type { BadgeColor } from '../../generic/Badge/Badge';

import styles from './VcsGitHubMigrationCard.module.less';

type Props = {
  org: Pick<Organization, 'title'>;
  /** Whether the surrounding Backfill accordion is open; gates all fetching. */
  isOpen: boolean;
};

const describeSkipped = (installation: VcsGitHubInstallationResult): string => {
  const alreadyConnected = installation.skippedProjects.filter((project) => project.reason === 'already-connected');
  const drifted = installation.skippedProjects.filter((project) => project.reason === 'vcs-host-drift');
  const parts = [];
  if (alreadyConnected.length > 0) {
    parts.push(`${alreadyConnected.length} already connected`);
  }
  if (drifted.length > 0) {
    parts.push(`${drifted.length} vcs host drift`);
  }
  return parts.join(', ');
};

const JOB_STATUS_BADGES: Record<VcsGitHubMigrationJob['status'], { label: string; color: BadgeColor }> = {
  CREATED: { label: 'Queued', color: 'light-gray' },
  ASSIGNED: { label: 'Assigned', color: 'light-blue' },
  RUNNING: { label: 'Running', color: 'blue' },
  SUCCEEDED: { label: 'Succeeded', color: 'green' },
  FAILED: { label: 'Failed', color: 'red' },
};

const PREVIEW_OUTCOME_LABELS: Record<VcsGitHubInstallationResult['outcome'], string> = {
  migrated: 'migrated',
  'would-migrate': 'would migrate',
  failed: 'failed',
};

const MIGRATION_STATE_BADGES: Record<VcsGitHubMigrationState, { label: string; color: BadgeColor }> = {
  unmigrated: { label: 'Not migrated', color: 'light-gray' },
  partial: { label: 'Partially migrated', color: 'yellow' },
  migrated: { label: 'Migrated', color: 'green' },
};

const JOB_COLUMNS: Column[] = [
  { Header: 'Task', accessor: 'id' },
  {
    Header: 'Status',
    accessor: 'status',
    Cell: ({ value }: { value: VcsGitHubMigrationJob['status'] }) => (
      <Badge color={JOB_STATUS_BADGES[value].color} size="small">{JOB_STATUS_BADGES[value].label}</Badge>
    ),
  },
  {
    Header: 'Queued',
    accessor: 'createdAt',
    Cell: ({ value }: { value: string }) => new Date(value).toLocaleString(),
  },
];

/** Inline dot between stats; hidden from screen readers and spaced by CSS rather than literal whitespace. */
const Separator: VoidFunctionComponent = () => <span aria-hidden className={styles.separator}>&middot;</span>;

type MigrationStatusProps = {
  status: ReturnType<typeof useGetVcsGitHubStatus>;
};

/** Status at a glance: the state line, the in-flight callout, both sides' counts, and the job history. */
const MigrationStatus: VoidFunctionComponent<MigrationStatusProps> = ({ status }) => {
  if (status.isLoading) {
    return <Loader size={30} />;
  }
  if (status.isError) {
    return (
      <Banner
        title="Failed to load the migration status"
        body={getErrorMessage(status.error, 'An unknown error occurred.')}
        bannerStyle={BannerStyle.High}
      />
    );
  }
  if (!status.data) {
    return null;
  }

  const { migrationState, isImportFlagEnabled, legacy, vcsService, recentJobs, activeJob } = status.data;
  const stateBadge = MIGRATION_STATE_BADGES[migrationState];

  return (
    <>
      {/* Carries the paragraph styles on a div rather than using Text.Paragraph: Badge renders a
          div, which is invalid inside a <p> and gets reparented out of the line by the browser.
          Badges are inline-flex, so the row still reads as one line. */}
      <div className="fossa-text fossa-text__paragraph">
        Status: <Badge color={stateBadge.color} size="micro">{stateBadge.label}</Badge>
        <Separator />
        Import flag: <strong>{isImportFlagEnabled ? 'Enabled' : 'Disabled'}</strong>
      </div>
      {activeJob && (
        <Text.Paragraph>
          A migration job is in flight: task {activeJob.id}
          <Separator />
          <strong>{JOB_STATUS_BADGES[activeJob.status].label}</strong>
          <Separator />
          queued {new Date(activeJob.createdAt).toLocaleString()}
        </Text.Paragraph>
      )}
      {migrationState === 'partial' && !activeJob && (
        <Text.Paragraph>
          Connections exist but no run has fully completed: the last run likely failed part-way.
          Migrate / Sync converges the rest.
        </Text.Paragraph>
      )}
      <Flex justify="left" gap={40}>
        <div>
          <Text.H5>Legacy GitHub App</Text.H5>
          <Text.Paragraph>
            <strong>{legacy.installationCount}</strong> installations
            <Separator />
            <strong>{legacy.projectCount}</strong> projects
          </Text.Paragraph>
        </div>
        <div>
          <Text.H5>vcs-service [read-app only]</Text.H5>
          <Text.Paragraph>
            <strong>{vcsService.connectionCount}</strong> connections
            <Separator />
            <strong>{vcsService.projectConnectionCount}</strong> linked projects
          </Text.Paragraph>
        </div>
      </Flex>
      {legacy.installationCount === 0 && (
        <Text.Paragraph>
          No legacy GitHub App installations found for this org, so there is nothing to migrate.
        </Text.Paragraph>
      )}
      {recentJobs.length > 0 && (
        <Accordion id="vcs-github-migration-jobs" isInitiallyOpen={false}>
          <Accordion.Target>Recent migration jobs ({recentJobs.length})</Accordion.Target>
          <Accordion.Body>
            <SimpleReactTable
              data={recentJobs}
              columns={JOB_COLUMNS}
              showPagination={false}
              isSortable={false}
              minRows={1}
            />
          </Accordion.Body>
        </Accordion>
      )}
    </>
  );
};

type MigrationPreviewProps = {
  preview: ReturnType<typeof useGetVcsGitHubMigrationPreview>;
};

/** The read-only dry-run manifest: per-installation outcome, project counts, and skip reasons. */
const MigrationPreview: VoidFunctionComponent<MigrationPreviewProps> = ({ preview }) => (
  <>
    {preview.isLoading && <Loader size={30} />}
    {preview.isError && (
      <Banner
        title="Failed to load the migration preview"
        body={getErrorMessage(preview.error, 'An unknown error occurred.')}
        bannerStyle={BannerStyle.High}
      />
    )}
    {preview.data && preview.data.installations.length === 0 && (
      <Text.Paragraph>The dry run found no legacy installations to migrate.</Text.Paragraph>
    )}
    {preview.data && preview.data.installations.length > 0 && (
      <ul>
        {preview.data.installations.map((installation) => (
          <li key={installation.gitHubInstallationId}>
            <strong>{installation.accountName}</strong>
            <Separator />
            installation <Text.Monospace>{installation.gitHubInstallationId}</Text.Monospace>
            {installation.suspended && <><Separator />suspended</>}
            <Separator />
            <strong>{PREVIEW_OUTCOME_LABELS[installation.outcome]}</strong>
            <Separator />
            {installation.createdProjects.length} projects to connect
            {installation.skippedProjects.length > 0 && <><Separator />skipped: {describeSkipped(installation)}</>}
            {installation.error !== null && <><Separator />error: {installation.error}</>}
          </li>
        ))}
      </ul>
    )}
  </>
);

/**
 * Operations on the current org for the legacy GitHub App -> vcs-service
 * migration: status at a glance, a read-only migration preview, and
 * migrate/rollback actions.
 */
export const VcsGitHubMigrationCard: VoidFunctionComponent<Props> = ({ org, isOpen }) => {
  const { onConfirm } = useDialogContext();
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(false);

  const status = useGetVcsGitHubStatus(isOpen);
  const preview = useGetVcsGitHubMigrationPreview(isOpen && isPreviewEnabled);
  const migrate = useMigrateVcsGitHubOrg();
  const rollback = useRollbackVcsGitHubOrg();

  // The rollback endpoint refuses while a migration job is queued or running: that run would
  // re-create the connections the rollback deletes. Disable rather than let the click 409.
  const activeJob = status.data?.activeJob ?? null;

  const hasLegacyInstallations = (status.data?.legacy.installationCount ?? 0) > 0;

  // Either action makes the dry-run manifest stale, so hide it and drop its cache; the next
  // "Run preview" fetches a fresh plan instead of flashing the pre-action one.
  const discardPreview = () => {
    setIsPreviewEnabled(false);
    queryClient.removeQueries({ queryKey: getVcsGitHubMigrationPreviewQueryKey() });
  };

  const onMigrateClick = () => {
    onConfirm({
      title: 'Migrate to vcs-service',
      body: (
        <div>
          This queues a background job that migrates {org.title}&apos;s legacy GitHub App projects to vcs-service.
          Re-running it is safe and converges anything imported since.
        </div>
      ),
      confirmText: 'Migrate / Sync',
      onConfirm: async () => {
        discardPreview();
        migrate.mutate();
      },
    });
  };

  const onRollbackClick = () => {
    if (!status.data) {
      return;
    }
    onConfirm({
      title: 'Roll back to the legacy GitHub App path?',
      body: (
        <div>
          <p>
            This disables the VCS Service import flag for {org.title} and deletes
            its {status.data.vcsService.connectionCount} fossaCore connections along
            with {status.data.vcsService.projectConnectionCount} project links.
          </p>
          <p>
            fossabot connections and vcs-service itself are untouched. Running Migrate / Sync again restores the org.
          </p>
        </div>
      ),
      confirmText: 'Rollback to legacy',
      onConfirm: async () => {
        discardPreview();
        rollback.mutate();
      },
    });
  };

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>VCS GitHub Migration</Card.Header>
      <Card.Content>
        <div className={styles.content}>
          <Text.Paragraph>
            Migrate this org&apos;s legacy GitHub App projects to vcs-service, or roll it back to the legacy path.
          </Text.Paragraph>

          <MigrationStatus status={status} />

          <Flex justify="left" gap={10}>
            {/* The status block above already spells out each of these states, so a disabled
                button here is never unexplained. */}
            <Button
              type={BUTTON_TYPE.OUTLINE}
              onClick={async () => {
                if (isPreviewEnabled) {
                  await preview.refetch();
                } else {
                  setIsPreviewEnabled(true);
                }
              }}
              isDisabled={preview.isFetching || !hasLegacyInstallations}
            >
              {isPreviewEnabled ? 'Refresh preview' : 'Run preview'}
            </Button>
            <Button
              onClick={onMigrateClick}
              isDisabled={migrate.isPending || !hasLegacyInstallations}
            >
              Migrate / Sync
            </Button>
            <Button
              type={BUTTON_TYPE.ERROR}
              onClick={onRollbackClick}
              isDisabled={
                rollback.isPending
                || !status.data
                || status.data.migrationState === 'unmigrated'
                || activeJob !== null
              }
            >
              Rollback to legacy
            </Button>
          </Flex>

          {isPreviewEnabled && (
            <>
              <Divider />
              <div className={styles.section}>
                <Text.Heading level={4}>Migration preview</Text.Heading>
                <Text.Paragraph>
                  A dry run of what Migrate / Sync would create or skip for each legacy installation.
                  Nothing is written.
                </Text.Paragraph>
              </div>
              <MigrationPreview preview={preview} />
            </>
          )}
        </div>
      </Card.Content>
    </Card>
  );
};
